import { useState, useMemo, useEffect, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { darkTheme } from '../../lib/tokens';
import { fetchBroadcasts, previewBroadcast, sendBroadcast } from './api';
import type { AudienceFilter, AudienceTier, BroadcastRecord } from './types';
import { ApiError } from '../../lib/api';
import { useStepUp } from '../auth/useStepUp';
import { useAppContextStore } from '../app/appContextStore';
import { appLabel, ALL_APPS, DEFAULT_APP_ID } from '../../lib/appConstants';
import {
  PUSH_TARGETS,
  deepLinkExamples,
  deepLinkPathList,
  isAppDeepLink,
  isKnownDeepLinkPath,
  schemePrefix,
} from '../../lib/deepLinks';

const TIERS: { value: AudienceTier; label: string }[] = [
  { value: 'ALL', label: 'Alle Nutzer' },
  { value: 'FREE', label: 'Nur Free' },
  { value: 'PAID', label: 'Nur Pro' },
];

export function BroadcastsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const stepUp = useStepUp();
  const [feedback, setFeedback] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [deepLink, setDeepLink] = useState('');
  const [tier, setTier] = useState<AudienceTier>('ALL');
  const [inactiveDays, setInactiveDays] = useState<number | ''>('');
  const [bypassPreferences, setBypassPreferences] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Ziel-App aus dem globalen App-Picker (TopBar) — app-gebundene Aktion.
  // Im "Alle Apps"-Modus braucht der Broadcast EINE App → Fallback currivium.
  const ctxApp = useAppContextStore((s) => s.appId);
  const app = ctxApp === ALL_APPS ? DEFAULT_APP_ID : ctxApp;

  // Der Push-Deep-Link laeuft NICHT ueber die Linking-Config, sondern ueber
  // parseDeepLink() in packages/core/src/hooks/useNotifications.ts — eine
  // eigene Pfad-Menge mit eigenem Schema pro App. Unbekanntes verwirft der
  // Handler still (PUSH_DEEPLINK_UNHANDLED), inklusive https-Links: die
  // Push-Strecke ruft kein openURL.
  const linkPrefix = schemePrefix(app);
  const linkExamples = useMemo(() => deepLinkExamples(app, PUSH_TARGETS), [app]);
  const linkPathList = useMemo(() => deepLinkPathList(PUSH_TARGETS), []);
  const trimmedLink = deepLink.trim();
  const linkKind: 'none' | 'deeplink' | 'unknown' = !trimmedLink
    ? 'none'
    : isAppDeepLink(trimmedLink, app) && isKnownDeepLinkPath(trimmedLink, app, PUSH_TARGETS)
      ? 'deeplink'
      : 'unknown';

  const audience = useMemo<AudienceFilter>(
    () => ({
      tier,
      inactiveDays: inactiveDays === '' ? undefined : Number(inactiveDays),
      app,
    }),
    [tier, inactiveDays, app],
  );

  // Live-Preview der Recipient-Zahl, debounced über tier+inactiveDays-Änderungen.
  const [debouncedAudience, setDebouncedAudience] = useState(audience);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedAudience(audience), 250);
    return () => clearTimeout(t);
  }, [audience]);

  const preview = useQuery({
    queryKey: ['broadcasts', 'preview', debouncedAudience],
    queryFn: () => previewBroadcast({ audience: debouncedAudience }),
  });

  const history = useQuery({
    queryKey: ['broadcasts', 'history'],
    queryFn: () => fetchBroadcasts(),
  });

  // Toast-Auto-Hide
  useEffect(() => {
    if (!feedback) return;
    const t = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(t);
  }, [feedback]);

  async function handleSend(e: FormEvent, opts: { testOnly?: boolean } = {}) {
    e.preventDefault();
    if (submitting) return;
    setFeedback(null);
    setSubmitting(true);

    try {
      // Ein Test-Push an einen selbst soll immer ankommen — sonst testet man
      // gegen die eigenen Ruhezeiten statt gegen den Broadcast.
      const bypass = opts.testOnly ? true : bypassPreferences;
      const payload = {
        title: title.trim(),
        body: body.trim(),
        deepLink: deepLink.trim() || undefined,
        audience: opts.testOnly ? { ...audience, testSendToSelf: true } : audience,
        bypassPreferences: bypass,
      };

      let stepUpToken: string | undefined;
      const recipients = opts.testOnly ? 1 : preview.data?.recipientCount ?? 0;
      // Umgehung ist unabhängig von der Größe step-up-pflichtig: sie erreicht
      // Nutzer, die Benachrichtigungen ausdrücklich abgeschaltet haben.
      if (!opts.testOnly && (recipients > 100 || bypass)) {
        stepUpToken = await stepUp.request({
          action: 'broadcast.sendAll',
          description: bypass
            ? `Service-Meldung an ${recipients} Empfänger — überschreibt deren Benachrichtigungs-Einstellungen und Ruhezeiten`
            : `Push an ${recipients} Empfänger versenden`,
          destructive: true,
        });
      }

      const sent = await sendBroadcast(payload, stepUpToken);
      const skipped = sent.skippedByPreferences ?? 0;
      setFeedback({
        type: 'ok',
        text: opts.testOnly
          ? 'Test-Push an dich gesendet.'
          : `Versendet: ${sent.successCount} erfolgreich, ${sent.failureCount} fehlgeschlagen (${sent.recipientCount} Empfänger` +
            `${skipped > 0 ? `, ${skipped} durch eigene Einstellungen ausgeschlossen` : ''}).`,
      });
      queryClient.invalidateQueries({ queryKey: ['broadcasts', 'history'] });
      if (!opts.testOnly) {
        setTitle('');
        setBody('');
        setDeepLink('');
      }
    } catch (err) {
      setFeedback({
        type: 'err',
        text: err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Fehlgeschlagen',
      });
    } finally {
      setSubmitting(false);
    }
  }

  const canSend = title.trim().length > 0 && body.trim().length > 0;

  return (
    <div className="mx-auto max-w-[1200px]">
      {stepUp.modal}
      {feedback && <Toast type={feedback.type} text={feedback.text} />}

      <header className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Push-Broadcasts</h1>
        <p className="mt-1 text-xs text-text-tertiary">
          OS-Notifications an aktive App-User. Erreicht auch passive User. &gt;100 Empfänger
          erfordern Step-up.
        </p>
      </header>

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <form
          onSubmit={(e) => handleSend(e)}
          className="rounded-md border p-5"
          style={{
            background: darkTheme.colors.bgSurface,
            borderColor: darkTheme.colors.borderSubtle,
          }}
        >
          <h2 className="mb-4 text-sm font-semibold text-text-primary">Neuer Push</h2>

          <div className="space-y-4">
            <Field label="Titel" hint={`${title.length}/80`}>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={80}
                required
                className={inputClass}
                style={{ borderColor: darkTheme.colors.borderSubtle }}
                placeholder="Was wartet auf dich?"
              />
            </Field>

            <Field label="Body" hint={`${body.length}/280`}>
              <textarea
                rows={3}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={280}
                required
                className={inputClass}
                style={{ borderColor: darkTheme.colors.borderSubtle }}
                placeholder="3 neue Lektionen sind bereit. Kommst du heute durch eine?"
              />
            </Field>

            <Field label="Deep-Link (optional)" hint={`nur ${linkPrefix}`}>
              <input
                type="text"
                value={deepLink}
                onChange={(e) => setDeepLink(e.target.value)}
                className={inputClass}
                style={{ borderColor: darkTheme.colors.borderSubtle }}
                placeholder={`${linkPrefix}learn/Lesson/abc123`}
              />
              {linkKind === 'deeplink' && (
                <p className="mt-1 text-[10px] text-text-tertiary">
                  Ziel in {appLabel(app)}. Beispiele:{' '}
                  {linkExamples.map((ex, i) => (
                    <span key={ex}>
                      {i > 0 ? ', ' : ''}
                      <code className="text-accent-light">{ex}</code>
                    </span>
                  ))}
                </p>
              )}
              {linkKind === 'unknown' && (
                <p className="mt-1 text-[10px]" style={{ color: darkTheme.colors.warning }}>
                  Der Push-Handler kennt dieses Ziel nicht und verwirft es beim Tippen
                  still — die Nachricht kommt an, öffnet aber nur die App. Externe
                  https-Links kann der Push nicht öffnen. Gültig:{' '}
                  <code>{linkPathList}</code>
                </p>
              )}
            </Field>

            <Field label="Ziel-App" hint="Oben im App-Picker wählbar — bestimmt Empfänger + Geräte">
              <span
                className="inline-block rounded-full px-3 py-1 text-xs"
                style={{
                  background: darkTheme.colors.accentSubtle,
                  color: darkTheme.colors.accentLight,
                  border: `1px solid ${darkTheme.colors.accent}`,
                }}
              >
                {appLabel(app)}
              </span>
            </Field>

            <Field label="Zielgruppe — Tier">
              <div className="flex flex-wrap gap-1.5">
                {TIERS.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setTier(t.value)}
                    className="rounded-full px-3 py-1 text-xs"
                    style={{
                      background: tier === t.value ? darkTheme.colors.accentSubtle : 'transparent',
                      color: tier === t.value ? darkTheme.colors.accentLight : darkTheme.colors.textSecondary,
                      border: `1px solid ${tier === t.value ? darkTheme.colors.accent : darkTheme.colors.borderSubtle}`,
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </Field>

            <Field
              label="Inaktiv seit (Tagen, optional)"
              hint="Nur User die seit X Tagen nicht aktiv waren"
            >
              <input
                type="number"
                min={0}
                max={365}
                value={inactiveDays}
                onChange={(e) =>
                  setInactiveDays(e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10)))
                }
                className={inputClass}
                style={{ borderColor: darkTheme.colors.borderSubtle }}
                placeholder="z.B. 7"
              />
            </Field>
          </div>

          {/* NF-4: Broadcasts respektieren jetzt Master-Toggle und Ruhezeiten.
              Die Umgehung ist bewusst eine sichtbare Entscheidung — vorher war
              sie stilles Standardverhalten. */}
          <label
            className="mt-4 flex cursor-pointer items-start gap-2.5 rounded-md border p-3"
            style={{
              borderColor: bypassPreferences
                ? darkTheme.colors.warning
                : darkTheme.colors.borderSubtle,
              background: bypassPreferences ? darkTheme.colors.warningBg : 'transparent',
            }}
          >
            <input
              type="checkbox"
              checked={bypassPreferences}
              onChange={(e) => setBypassPreferences(e.target.checked)}
              className="mt-0.5"
            />
            <span className="text-xs">
              <span className="font-semibold" style={{ color: darkTheme.colors.textPrimary }}>
                Als Service-Meldung senden
              </span>
              <span className="block" style={{ color: darkTheme.colors.textSecondary }}>
                Erreicht auch Nutzer, die Benachrichtigungen abgeschaltet haben, und ignoriert
                deren Ruhezeiten. Nur für Ausfälle und Sicherheitshinweise — nicht für
                Redaktionelles. Erfordert eine zusätzliche Bestätigung und wird protokolliert.
              </span>
            </span>
          </label>

          <div
            className="mt-5 flex items-center justify-between gap-3 border-t pt-4"
            style={{ borderColor: darkTheme.colors.borderSubtle }}
          >
            <PreviewBadge
              recipientCount={preview.data?.recipientCount}
              tokenCount={preview.data?.tokenCount}
              loading={preview.isFetching}
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={(e) => handleSend(e, { testOnly: true })}
                disabled={submitting || !canSend}
                className="rounded-md border border-border-subtle bg-bg-elevated px-3 py-1.5 text-xs text-text-primary hover:bg-bg-overlay disabled:opacity-50"
              >
                Test an mich
              </button>
              <button
                type="submit"
                disabled={submitting || !canSend || preview.data?.recipientCount === 0}
                className="rounded-md px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                style={{ background: darkTheme.colors.accent }}
              >
                {submitting ? 'Sende …' : 'An Audience senden'}
              </button>
            </div>
          </div>
        </form>

        <aside
          className="rounded-md border p-5"
          style={{
            background: darkTheme.colors.bgSurface,
            borderColor: darkTheme.colors.borderSubtle,
          }}
        >
          <h2 className="mb-3 text-sm font-semibold text-text-primary">Vorschau</h2>
          <PhonePreview title={title} body={body} />
          <p className="mt-3 text-[11px] text-text-tertiary">
            iOS/Android zeigen den Push auf Lockscreen + Banner. Bei längerem Body wird gekürzt.
          </p>
        </aside>
      </div>

      <section className="mt-8">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
          Verlauf
        </h2>
        {history.isLoading && <SkeletonList />}
        {history.data && history.data.length === 0 && (
          <div
            className="rounded-md border p-6 text-center text-sm text-text-tertiary"
            style={{
              background: darkTheme.colors.bgSurface,
              borderColor: darkTheme.colors.borderSubtle,
            }}
          >
            Noch keine Pushes versendet.
          </div>
        )}
        {history.data && history.data.length > 0 && (
          <div className="space-y-2">
            {history.data.map((b) => (
              <HistoryRow key={b.id} broadcast={b} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

const inputClass =
  'w-full rounded-md border bg-bg-base px-3 py-2 text-sm text-text-primary outline-none focus:border-accent';

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-text-secondary">{label}</span>
        {hint && <span className="text-[10px] text-text-tertiary">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function PreviewBadge({
  recipientCount,
  tokenCount,
  loading,
}: {
  recipientCount?: number;
  tokenCount?: number;
  loading: boolean;
}): JSX.Element {
  if (loading) return <span className="text-[11px] text-text-tertiary">Berechne …</span>;
  if (recipientCount === undefined) return <span />;
  return (
    <div className="text-[11px] text-text-secondary">
      <span className="font-mono text-text-primary">{recipientCount}</span> User ·{' '}
      <span className="font-mono text-text-primary">{tokenCount ?? 0}</span> Tokens
      {(tokenCount ?? 0) === 0 && recipientCount > 0 && (
        <span className="ml-2 text-warning">⚠ keine Push-Tokens registriert</span>
      )}
    </div>
  );
}

function PhonePreview({ title, body }: { title: string; body: string }): JSX.Element {
  return (
    <div
      className="rounded-2xl p-3"
      style={{ background: darkTheme.colors.bgBase }}
    >
      <div
        className="rounded-xl border p-3"
        style={{
          background: 'rgba(255,255,255,0.04)',
          borderColor: darkTheme.colors.borderSubtle,
          backdropFilter: 'blur(8px)',
        }}
      >
        <div className="flex items-start gap-2">
          <div
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sm font-extrabold text-white"
            style={{
              background: `linear-gradient(135deg, ${darkTheme.colors.accent}, ${darkTheme.colors.accentLight})`,
            }}
          >
            C
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-[11px] font-semibold uppercase text-text-secondary">
                Currivium
              </span>
              <span className="text-[10px] text-text-tertiary">jetzt</span>
            </div>
            <div className="mt-0.5 truncate text-sm font-semibold text-text-primary">
              {title || 'Titel des Pushes'}
            </div>
            <div className="mt-0.5 line-clamp-3 text-xs text-text-secondary">
              {body || 'Body-Text — wird auf Lockscreen + Banner angezeigt.'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function HistoryRow({ broadcast }: { broadcast: BroadcastRecord }): JSX.Element {
  const successPct =
    broadcast.recipientCount > 0
      ? Math.round((broadcast.successCount / broadcast.recipientCount) * 100)
      : 0;
  return (
    <div
      className="rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-semibold text-text-primary">{broadcast.title}</div>
        <div className="text-[11px] text-text-tertiary">{formatDate(broadcast.sentAt)}</div>
      </div>
      <div className="mt-1 line-clamp-2 text-xs text-text-secondary">{broadcast.body}</div>
      <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-text-tertiary">
        <span>
          Audience:{' '}
          <span className="font-mono text-text-primary">
            {broadcast.audience.tier}
            {broadcast.audience.inactiveDays !== undefined &&
              ` · inaktiv ${broadcast.audience.inactiveDays}d`}
          </span>
        </span>
        <span>
          {broadcast.successCount}/{broadcast.recipientCount} erfolgreich ({successPct}%)
        </span>
        {broadcast.failureCount > 0 && (
          <span className="text-warning">{broadcast.failureCount} fehlgeschlagen</span>
        )}
        {broadcast.sender && <span>von {broadcast.sender.email}</span>}
      </div>
    </div>
  );
}

function SkeletonList(): JSX.Element {
  return (
    <div className="space-y-2">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="h-24 animate-pulse rounded-md"
          style={{ background: darkTheme.colors.bgSurface }}
        />
      ))}
    </div>
  );
}

function Toast({ type, text }: { type: 'ok' | 'err'; text: string }): JSX.Element {
  return (
    <div
      role="status"
      className="fixed right-6 top-20 z-50 rounded-md px-4 py-2 text-sm shadow-lg"
      style={{
        background: type === 'ok' ? darkTheme.colors.successBg : darkTheme.colors.dangerBg,
        color: type === 'ok' ? darkTheme.colors.success : darkTheme.colors.danger,
      }}
    >
      {text}
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
